import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Mail, ShieldAlert, Save, PlayCircle, Radio } from 'lucide-react';

/**
 * Réglage du mode test des emails d'onboarding prospects.
 * Quand activé, tous les emails d'onboarding sont redirigés vers l'adresse
 * saisie (au lieu d'être envoyés au vrai candidat). Utile en dev/preview.
 * En production, ce mode doit être désactivé.
 */
export default function ProspectTestEmailSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/prospect/test-email');
      setSettings(r.data);
      setEnabled(!!r.data.enabled);
      setEmail(r.data.email || '');
    } catch (e) {
      toast.error("Impossible de charger le réglage");
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (enabled && !email.trim()) {
      toast.error("Renseignez une adresse email de redirection");
      return;
    }
    setSaving(true);
    try {
      await api.put('/prospect/test-email', { enabled, email: enabled ? email.trim() : null });
      toast.success(enabled
        ? `Mode test activé — les emails partiront vers ${email.trim()}`
        : 'Mode test désactivé — les emails partiront aux vrais candidats');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
    </div>
  );

  const isLive = !enabled;
  const effective = settings?.effective_email;

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="prospect-test-email-card">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isLive ? 'bg-green-50' : 'bg-amber-50'}`}>
          {isLive ? <Radio className="w-5 h-5 text-green-600" /> : <ShieldAlert className="w-5 h-5 text-amber-600" />}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-slate-900">Emails d'onboarding prospects</h2>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${isLive ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
              {isLive ? '● Envoi RÉEL' : '● Mode test'}
            </span>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {isLive
              ? 'Les emails sont envoyés directement aux candidats. Vérifiez bien le contenu avant de scanner en masse.'
              : `Tous les emails d'onboarding sont redirigés vers ${effective || '(email non défini)'}. Idéal pour tester sans spammer les vrais candidats.`}
          </p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setEnabled(false)}
            className={`px-4 py-3 rounded-lg border text-sm text-left ${!enabled ? 'border-green-600 bg-green-50 text-green-900' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
            data-testid="prospect-test-mode-live"
          >
            <div className="flex items-center gap-2 font-semibold">
              <PlayCircle className="w-4 h-4" />
              Envoi réel
            </div>
            <div className="text-xs opacity-70 mt-1">Production — envoi aux vrais candidats</div>
          </button>
          <button
            type="button"
            onClick={() => setEnabled(true)}
            className={`px-4 py-3 rounded-lg border text-sm text-left ${enabled ? 'border-amber-600 bg-amber-50 text-amber-900' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
            data-testid="prospect-test-mode-test"
          >
            <div className="flex items-center gap-2 font-semibold">
              <ShieldAlert className="w-4 h-4" />
              Mode test
            </div>
            <div className="text-xs opacity-70 mt-1">Redirige tout vers une adresse de test</div>
          </button>
        </div>

        {enabled && (
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Adresse email de redirection</label>
            <div className="relative mt-1">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ex : renoresolv@gmail.com"
                className="pl-9"
                data-testid="prospect-test-email-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Tous les emails d'onboarding (initial + relances J+2) partiront vers cette adresse au lieu du candidat.
              Une bannière « Mode test » est ajoutée à l'email avec l'adresse réelle du candidat.
            </p>
          </div>
        )}

        {settings?.env_fallback_present && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
            <b>Note technique :</b> une variable d'environnement <code>PROSPECT_TEST_EMAIL_OVERRIDE</code> est définie
            {settings.env_fallback_present && ` (${settings.email})`}. Le réglage UI ci-dessus la remplace. Pour un
            comportement 100 % piloté par l'UI, retirez cette variable de la configuration du serveur.
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="prospect-test-email-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}
